import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type SeasonTicketCodeStatus = 'PENDING_VALIDATION' | 'ACTIVE' | 'REVOKED' | 'UNLINKED';

export interface SeasonTicketCodeDto {
  id: string;
  code: string;
  status: SeasonTicketCodeStatus;
  seat: string | null;
  externalValidUntil: string | null;
  googleWalletLink: string | null;
  appleWalletLink: string | null;
  linkedAt: string;
  lastValidatedAt: string | null;
  lastValidationResult: string | null;
  // Contatore della regola "2 KO consecutivi" (T-269): a 1 la tessera sta per essere revocata.
  consecutiveKoDays: number;
  lastKoAt: string | null;
  revokedAt: string | null;
  unlinkedAt: string | null;
  unlinkedBy: string | null;
  unlinkReason: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
  };
}

export interface PaginatedSeasonTickets {
  data: SeasonTicketCodeDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface SeasonTicketsFilters {
  page?: number;
  limit?: number;
  status?: SeasonTicketCodeStatus;
  search?: string;
}

const QK = {
  list: (filters: SeasonTicketsFilters) => ['season-tickets', filters] as const,
};

export function useSeasonTickets(filters: SeasonTicketsFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const res = await apiClient.get<PaginatedSeasonTickets>('/season-tickets/admin', { params });
      return res.data;
    },
  });
}

export interface AdminLinkSeasonTicketInput {
  userId: string;
  code: string;
  seat?: string;
}

export function useAdminLinkSeasonTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminLinkSeasonTicketInput) => {
      const res = await apiClient.post<SeasonTicketCodeDto>('/season-tickets/admin/link', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['season-tickets'] });
    },
  });
}

export function useUnlinkSeasonTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiClient.post<SeasonTicketCodeDto>(
        `/season-tickets/admin/${id}/unlink`,
        { reason },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['season-tickets'] });
    },
  });
}

export function useRevalidateSeasonTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<SeasonTicketCodeDto>(
        `/season-tickets/admin/${id}/revalidate`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['season-tickets'] });
    },
  });
}
